import { useEffect, useMemo, useState } from "react";

import { ensureCsrfToken} from "../api";

import { showVendorUploadSummary } from "../utils/vendorUploadSummary";


import Swal from "sweetalert2";

const dataTypeOptions = [
  { value: "migo", label: "MIGO" },
  { value: "po", label: "Purchase Order (PO)" },
  // { value: "vendormaster", label: "Vendor Master" },
  
];

function formatCellValue(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

export default function InvoiceUploadDataView() {
  const [dataType, setDataType] = useState("");
  const [file, setFile] = useState(null);
  const [tableRows, setTableRows] = useState([]);
  const [tooltips, setTooltips] = useState({});
  const [loadingTable, setLoadingTable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [missingData, setMissingData] = useState([]);
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);

  console.log("MODAL STATE:", {
    showVendorModal,
    selectedVendor,
  });

  const headers = useMemo(() => {
    if (!tableRows.length) {
      return [];
    }
    return Object.keys(tableRows[0]);
  }, [tableRows]);

  useEffect(() => {
    if (!showVendorModal) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowVendorModal(false);
        setSelectedVendor(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showVendorModal]);

  async function handleVendorSelect(vendorCode) {
    try {
      console.log("1. Selected vendor code:", vendorCode);

      const response = await fetch(
        `/api/vendor-master/details/?vendor_code=${encodeURIComponent(vendorCode)}`,
        {
          credentials: "include",
        }
      );

      console.log("2. Response status:", response.status);

      const payload = await response.json();

      console.log("3. Vendor payload:", payload);

      if (!response.ok || payload.status === "error") {
        throw new Error(
          payload.message || "Unable to load vendor."
        );
      }

      console.log("4. Setting vendor:", payload.vendor);

      setSelectedVendor(payload.vendor);

      setVendorSearch(
        `${payload.vendor.VendorCode} - ${payload.vendor.VendorName}`
      );

      setVendorSuggestions([]);

      console.log("5. Opening modal");

      setShowVendorModal(true);

    } catch (error) {
      console.error("Vendor select error:", error);
      alert(error.message);
    }
  }

  
  async function handleVendorSearch(value) {
    setVendorSearch(value);

    if (!value.trim()) {
      setVendorSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `/api/vendor-master/search/?q=${encodeURIComponent(value)}`,
        {
          credentials: "include",
        }
      );

      const payload = await response.json();

      setVendorSuggestions(payload.vendors || []);
    } catch (error) {
      console.error("Vendor search failed:", error);
      setVendorSuggestions([]);
    }
  }

  async function handleVendorSave() {
    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/vendor-master/update/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          VendorCode: selectedVendor.VendorCode,
          VendorName: selectedVendor.VendorName,
          GSTNo: selectedVendor.GSTNo,
          LDCNo: selectedVendor.LDCNo,
          LDCThreshold: selectedVendor.LDCThreshold,
          LDCStartDate: selectedVendor.LDCStartDate,
          LDCEndDate: selectedVendor.LDCEndDate,
          LDCWTCode: selectedVendor.LDCWTCode,
          BlockedYN: selectedVendor.BlockedYN,
          MSMERegisteredYN: selectedVendor.MSMERegisteredYN,
          MSMENumber: selectedVendor.MSMENumber,
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload.status === "error") {
        throw new Error(
          payload.message || "Unable to update vendor."
        );
      }

      await Swal.fire({
        icon: "success",
        title: "Vendor Updated",
        text: payload.message || "Vendor updated successfully.",
        confirmButtonText: "OK",
      });

      setShowVendorModal(false);
      setSelectedVendor(null);

      await loadSystemData("vendormaster");

    } catch (error) {

      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.message ||
          "Failed to update vendor.",
        confirmButtonText: "OK",
      });

    }
  }

  async function refreshMissingSystemData() {
    try {
      const response = await fetch("/api/missingsytem-data/", {
        credentials: "include",
      });
      const payload = await response.json();
      if (!response.ok || payload.success === false) {
        return;
      }
      setMissingData(Array.isArray(payload.missing) ? payload.missing : []);
    } catch (requestError) {
      setMissingData([]);
    }
  }

  async function loadSystemData(type, { clearMessages = true } = {}) {
    if (!type) {
      setTableRows([]);
      setTooltips({});
      return;
    }

    setLoadingTable(true);
    setError("");
    if (clearMessages) {
      setStatus("");
    }

    try {
      const response = await fetch(`/api/inv/fetch-data/?type=${encodeURIComponent(type)}`, {
        credentials: "include",
      });
      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok || payload?.error) {
        throw new Error(payload?.error || `Unable to load ${type} data.`);
      }

      setTableRows(payload?.data || []);
      setTooltips(payload?.tooltips || {});
    } catch (requestError) {
      setTableRows([]);
      setTooltips({});
      setError(requestError.message || "Unable to load system data.");
    } finally {
      setLoadingTable(false);
    }
  }

  useEffect(() => {
    if (!dataType) {
      setTableRows([]);
      setTooltips({});
      return;
    }

    loadSystemData(dataType);
  }, [dataType]);

  useEffect(() => {
    refreshMissingSystemData();
  }, []);

  async function handleUpload(event) {
    event.preventDefault();
    setError("");

    if (!dataType) {
      await Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select a data type.",
      });
      return;
    }

    if (!file) {
      await Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please choose a file to upload.",
      });
      return;
    }

    setSubmitting(true);

    try {
      const csrfToken = await ensureCsrfToken();

      const formData = new FormData();
      formData.append("data_type", dataType);
      formData.append("excel_file", file);

      const response = await fetch("/api/inv/upload-data/", {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        body: formData,
      });

      const isJson = response.headers
        .get("content-type")
        ?.includes("application/json");

      const payload = isJson
        ? await response.json()
        : null;

      if (!isJson) {
        throw new Error(
          "Upload did not return JSON. Please verify the selected file and server logs."
        );
      }

      if (!response.ok || payload?.error) {
        throw new Error(
          payload?.error ||
          payload?.message ||
          "Unable to upload file."
        );
      }

      if (dataType === "vendormaster") {
        await showVendorUploadSummary(payload);
        
      } else {
        await Swal.fire({
          icon: "success",
          title: "Upload Successful",
          text:
            payload?.message ||
            "File uploaded successfully.",
          confirmButtonText: "OK",
        });
      }

      setFile(null);

      if (dataType) {
        await loadSystemData(dataType, {
          clearMessages: false,
        });
      }

      await refreshMissingSystemData();

    } catch (requestError) {

      await Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          requestError.message ||
          "Something went wrong while uploading the file.",
        confirmButtonText: "OK",
      });

      setError(
        requestError.message ||
        "Something went wrong while uploading the file."
      );

    } finally {
      setSubmitting(false);
    }
  }

 

  return (
    <section className="processor-view">
      {missingData.length ? (
        <div className="config-banner config-banner--error">
          Missing system data: {missingData.join(", ")}. Upload these datasets to avoid invoice processing gaps.
        </div>
      ) : null}

      <div className="upload-grid upload-grid--stacked">
        <section className="upload-card">
          <div className="upload-card__head">
            <h3>Upload Data File</h3>
            <p>Select a data category, upload the source file, and let Django process it.</p>
          </div>

          <form className="gl-form upload-form" onSubmit={handleUpload}>
            <label className="config-field">
              <span>Data Type</span>
              <select
                value={dataType}
                onChange={(event) => setDataType(event.target.value)}
                required
              >
                <option value="">-- Select Type --</option>
                {dataTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="config-field">
              <span>Source File</span>
              <input
                type="file"
                accept=".xlsx,.xls,.json"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
                required
              />
            </label>

            <div className="upload-form__button">
              <button
                className="primary-button"
                type="submit"
                disabled={submitting}
              >
                {submitting ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>

          <p className="upload-card__meta">
            {file
              ? `Selected file: ${file.name}`
              : "Accepted formats: .xlsx, .xls, .json"}
          </p>
        </section>

        <section className="workspace-panel">
          <div className="processor-table__header">
            <div>
              <p className="eyebrow">{dataType}</p>
            </div>

            {dataType ? (
              <button
                className="ghost-button"
                type="button"
                onClick={() => loadSystemData(dataType)}
                disabled={loadingTable}
              >
                {loadingTable ? "Refreshing..." : "Refresh"}
              </button>
            ) : null}
          </div>

          {dataType === "vendormaster" && (
            <div className="vendor-search">
              <input
                type="text"
                placeholder="Search by Vendor Code or Vendor Name"
                value={vendorSearch}
                onChange={(e) => handleVendorSearch(e.target.value)}
              />

              {vendorSuggestions.length > 0 && (
                <div className="vendor-suggestions">
                  {vendorSuggestions.map((vendor) => (
                    <div
                      key={vendor.VendorCode}
                      className="vendor-suggestion-item"
                      onClick={() => handleVendorSelect(vendor.VendorCode)}
                    >
                      <strong>{vendor.VendorCode}</strong>
                      {" - "}
                      {vendor.VendorName}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {dataType === "migo" ? (
            <div className="mapping-note-card">
              <strong>Note</strong>
              <p>
                The headers below are system-defined headers. Verify one invoice with the uploaded
                data to confirm the header mapping is correct in basic configuration.
              </p>
            </div>
          ) : null}

          {loadingTable ? (
            <p className="config-note">Loading system data…</p>
          ) : !dataType ? (
            <p className="empty-state">Choose a data type to load the corresponding system data preview.</p>
          ) : headers.length ? (
            <div className="config-table processor-table">
              <table className="systemdata-table">
                <thead>
                  <tr>
                    {headers.map((header) => (
                      <th key={header} title={tooltips[header] || ""}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row, index) => (
                    <tr key={`${dataType}-${index}`}>
                      {headers.map((header) => (
                        <td key={`${index}-${header}`}>{formatCellValue(row[header])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No data found for the selected type.</p>
          )}
        </section>
      
      </div>

      {showVendorModal && selectedVendor && (
        <div className="modal-overlay">
          <div className="modal-content">

            <div className="modal-header">
              <h3>Edit Vendor</h3>
            </div>

            <div className="modal-body">

              <label>
                Vendor Name
                <input
                  type="text"
                  value={selectedVendor.VendorName || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      VendorName: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                Vendor Code
                <input
                  type="text"
                  value={selectedVendor.VendorCode || ""}
                  disabled
                />
              </label>

              <label>
                GST No
                <input
                  type="text"
                  value={selectedVendor.GSTNo || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      GSTNo: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                LDC No
                <input
                  type="text"
                  value={selectedVendor.LDCNo || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      LDCNo: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                LDC Threshold
                <input
                  type="text"
                  value={selectedVendor.LDCThreshold || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      LDCThreshold: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                LDC Start Date
                <input
                  type="text"
                  value={selectedVendor.LDCStartDate || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      LDCStartDate: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                LDC End Date
                <input
                  type="text"
                  value={selectedVendor.LDCEndDate || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      LDCEndDate: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                LDC WT Code
                <input
                  type="text"
                  value={selectedVendor.LDCWTCode || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      LDCWTCode: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                Blocked Y/N
                <input
                  type="text"
                  value={selectedVendor.BlockedYN || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      BlockedYN: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                MSME Registered Y/N
                <input
                  type="text"
                  value={selectedVendor.MSMERegisteredYN || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      MSMERegisteredYN: e.target.value,
                    })
                  }
                />
              </label>

              <label>
                MSME Number
                <input
                  type="text"
                  value={selectedVendor.MSMENumber || ""}
                  onChange={(e) =>
                    setSelectedVendor({
                      ...selectedVendor,
                      MSMENumber: e.target.value,
                    })
                  }
                />
              </label>

            </div>

            <div className="modal-footer">

              <button
                className="primary-button"
                type="button"
                onClick={handleVendorSave}
              >
                Save
              </button>

              <button
                className="ghost-button"
                type="button"
                onClick={() => {
                  setShowVendorModal(false);
                  setSelectedVendor(null);
                }}
              >
                Cancel
              </button>

            </div>

          </div>
        </div>
      )}

    
    </section>
  );
}
